import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { router, useLocalSearchParams } from "expo-router";
import { FlatList, Platform } from "react-native";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LButton from "@components/LButton";
import LPressable from "@components/LPressable";
import LScreen from "@components/LScreen";
import {
  useMusicSyncGetPlaylistsPaginated,
  ExternalPlaylist,
} from "@hooks/trpc/useMusicSync";
import { z } from "zod";
import { STREAMING_SERVICES } from "./constants";
import TickBox from "@components/TickBox";
import LArtwork, { musicPlaceholders } from "@components/LArtwork";
import { theme } from "@theme";
import { ActivityIndicator } from "react-native";
import { useOnboardingStore } from "./store/onboardingStore";
import { captureException } from "@utils/captureException";
import { trackOnb, trackDirectImport } from "./analytics/trackOnboarding";
import { toast } from "sonner-native";

const AuthCallbackDataSchema = z.object({
  integrationUserUUID: z.string().uuid().nullable(),
  authModel: z.object({
    uuid: z.string().uuid(),
    status: z.enum(["initialised", "error", "success"]),
    error: z.string().optional().nullable(),
  }),
  integration: z.object({
    type: z.enum(STREAMING_SERVICES),
    returnUrl: z.string().url(),
  }),
});

export const data64ToJsonData = (data64: string) => {
  try {
    const data = JSON.parse(atob(data64));
    return AuthCallbackDataSchema.parse(data);
  } catch (error) {
    console.warn("::data64ToJsonData::", error);
    return null;
  }
};

export default function PickExternalPlaylists() {
  const { data64 } = useLocalSearchParams<{ data64?: string }>();
  const journey = useOnboardingStore((s) => s.journey) ?? "signup";
  const trackFn = (
    screen: string,
    action: string,
    props?: Record<string, unknown>,
  ) =>
    journey === "direct-import"
      ? trackDirectImport(screen, action, props)
      : trackOnb(journey, screen, action, props);

  // Derive parsed auth data directly from the route param. Only successful
  // callbacks that carry an integrationUserUUID are usable for the query.
  const authData = useMemo<z.infer<
    typeof AuthCallbackDataSchema
  > | null>(() => {
    if (!data64) return null;
    const data = data64ToJsonData(data64);
    if (
      data &&
      data.authModel.status === "success" &&
      data.integrationUserUUID
    ) {
      return data;
    }
    return null;
  }, [data64]);
  const [excludedPlaylists, setExcludedPlaylists] = useState<
    Map<string, ExternalPlaylist>
  >(new Map());

  const isQueryEnabled = Boolean(
    authData?.integrationUserUUID && authData?.integration?.type,
  );

  const {
    query: {
      data: paginatedData,
      error: playlistsError,
      isLoading: playlistsIsLoading,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    },
  } = useMusicSyncGetPlaylistsPaginated(
    authData?.integrationUserUUID && authData?.integration?.type
      ? {
          integrationUserUUID: authData.integrationUserUUID,
          service: authData.integration.type,
        }
      : undefined,
    {
      enabled: isQueryEnabled,
      staleTime: 0,
    },
  );

  const onError = useCallback((message?: string) => {
    toast.error(message ?? "Failed to connect to service. Please try again.");
    setTimeout(
      () => {
        router.replace("/onboarding/link-streaming-service");
      },
      Platform.OS === "web" ? 300 : 0,
    );
  }, []);

  const playlistsData = useMemo(
    () => paginatedData?.pages.flatMap((page) => page.data) ?? [],
    [paginatedData?.pages],
  );

  // Track view when playlists first load (once)
  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (playlistsData.length > 0 && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackFn("pick-playlists", "view", {
        dsp: authData?.integration?.type,
        count: playlistsData.length,
      });
    }
  }, [playlistsData.length, journey, authData?.integration?.type]);

  useEffect(() => {
    // If the first page of paginatedData exists but contains no playlists, show an error and exit early.
    if (
      paginatedData !== undefined &&
      paginatedData.pages.at(0)?.data?.length === 0
    ) {
      trackFn("pick-playlists", "load-error", {
        dsp: authData?.integration?.type,
        error: "no_playlists_found",
      });
      onError("We were unable to find any music. Please try again.");
      return;
    }
  }, [paginatedData?.pages.length]);

  useEffect(() => {
    // Only show error if query is enabled and not currently loading/fetching.
    if (
      playlistsError &&
      isQueryEnabled &&
      !playlistsIsLoading &&
      !isFetchingNextPage
    ) {
      let message: string | undefined = undefined;

      if (
        "data" in playlistsError &&
        typeof playlistsError.data === "object" &&
        playlistsError.data !== null
      ) {
        const errorData = playlistsError.data as {
          httpStatus?: number;
          stack?: string;
        };
        if (errorData.httpStatus === 404) {
          message = errorData.stack?.includes("channelNotFound")
            ? "No YouTube channel found. Please try again."
            : "We were unable to find any music. Please try again.";
        }
      }
      console.error("::PickExternalPlaylists:: playlistsError::", message);
      captureException(playlistsError, {
        channel: "error",
        area: "onboarding",
        operation: "load-external-playlists",
        tags: { dsp: authData?.integration?.type ?? "unknown" },
      });
      trackFn("pick-playlists", "load-error", {
        dsp: authData?.integration?.type,
        error: message ?? "fetch_failed",
      });
      onError(message);
    }
  }, [playlistsError, isQueryEnabled, playlistsIsLoading, isFetchingNextPage]);

  const handleContinue = () => {
    if (!authData?.integrationUserUUID) return;

    const excludedPlaylistObjects = Array.from(excludedPlaylists.values());
    const totalAvailable = playlistsData.length;
    const excludedCount = excludedPlaylistObjects.length;
    const importedCount = totalAvailable - excludedCount;

    trackFn("pick-playlists", "import-pressed", {
      total: totalAvailable,
      imported: importedCount,
      excluded: excludedCount,
    });

    useOnboardingStore
      .getState()
      .setExcludedPlaylists(
        excludedPlaylistObjects,
        authData.integration.type,
        authData.integrationUserUUID,
      );

    router.replace("/onboarding/loading?source=pick-playlists");
  };

  const togglePlaylistSelection = (playlistId: string) => {
    const newExcluded = new Map(excludedPlaylists);
    const wasExcluded = newExcluded.has(playlistId);
    if (wasExcluded) {
      newExcluded.delete(playlistId);
    } else {
      const playlist = playlistsData?.find((p) => p.id === playlistId);
      if (playlist) {
        newExcluded.set(playlistId, playlist);
      }
    }
    trackFn("pick-playlists", "playlist-toggled", {
      included: wasExcluded,
    });
    setExcludedPlaylists(newExcluded);
  };

  useEffect(() => {
    if (!data64) {
      onError("The connection result is missing. Please try again.");
      return;
    }

    const data = data64ToJsonData(data64);
    if (!data) {
      console.error("Invalid callback data");
      onError();
      return;
    }

    if (data.authModel.status === "success") {
      const { integrationUserUUID } = data;

      if (integrationUserUUID) {
        // Signup and direct-import: skip playlist selection, import everything
        if (journey === "signup" || journey === "direct-import") {
          useOnboardingStore
            .getState()
            .setExcludedPlaylists(
              [],
              data.integration.type,
              integrationUserUUID,
            );
          router.replace("/onboarding/loading?source=pick-playlists");
          return;
        }

        // Otherwise the parsed data is exposed via the `authData` memo, which
        // enables the playlists query for manual selection.
      } else {
        onError("The connection is incomplete. Please try again.");
      }
    } else {
      console.error("::Music API auth error:: ", data.authModel.error);
      trackFn("oauth", "error", {
        dsp: data.integration.type,
        error: data.authModel.error ?? data.authModel.status,
      });
      onError();
    }
  }, [data64]);

  return (
    <LScreen safeArea pt="l3" gap="m2" pb="l1">
      <LBox gap="s2" mb="m1">
        <LText variant="h2" textAlign="center">
          We found your Playlists
        </LText>
        <LText
          variant="bodyM2"
          fontWeight="500"
          color="whiteTranslucent50"
          textAlign="center"
        >
          Choose what you want to import
        </LText>
      </LBox>

      <FlatList
        data={playlistsData}
        keyExtractor={(item) => item.id}
        renderItem={({ item, index }) => (
          <LPressable
            sentryEventName={null}
            onPress={() => togglePlaylistSelection(item.id)}
          >
            <LBox flexDirection="row" alignItems="center">
              <LBox flex={1} gap="s3" alignItems="center" flexDirection="row">
                <LArtwork
                  size={40}
                  url={item.imageUrl as string}
                  placeholderImage={
                    musicPlaceholders[index % musicPlaceholders.length]
                  }
                />
                <LBox flex={1} gap="s1">
                  <LText variant="bodyM2ml" color="white" numberOfLines={1}>
                    {item.name}
                  </LText>
                  <LText
                    variant="bodyMSemiBold"
                    fontWeight="400"
                    color="greyText"
                  >
                    {item.totalItems} songs
                  </LText>
                </LBox>
              </LBox>
              <TickBox
                on={!excludedPlaylists.has(item.id)}
                toggle={() => togglePlaylistSelection(item.id)}
                title={item.name}
                variant="round"
              />
            </LBox>
          </LPressable>
        )}
        onEndReached={async () => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage().catch((error) => {
              console.error("Error fetching next page:", error);
            });
          }
        }}
        onEndReachedThreshold={0.4}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: theme.spacing.l1,
          gap: theme.spacing.s3,
        }}
        ListEmptyComponent={
          playlistsIsLoading ? (
            <LBox flex={1} justifyContent="center" alignItems="center" gap="s2">
              <ActivityIndicator size="small" color={theme.colors.greyText} />
              <LText color="greyText" fontWeight="500">
                Loading your playlists...
              </LText>
            </LBox>
          ) : null
        }
      />
      <LBox pt="m1" gap="m1">
        <LButton
          sentryEventName={null}
          variant="primary"
          label="IMPORT SELECTED"
          onPress={handleContinue}
          enabled={playlistsData.length > 0}
          borderRadius={"max"}
        />
      </LBox>
    </LScreen>
  );
}
